import React, { useEffect, useMemo, useRef, useState } from "react";
import Panel from "../../../components/ui/Panel/Panel.jsx";
import Button from "../../../components/ui/Button/Button.jsx";
import FormField from "../../../components/forms/FormField/FormField.jsx";
import TextInput from "../../../components/forms/TextInput/TextInput.jsx";
import Select from "../../../components/forms/Select/Select.jsx";
import Checkbox from "../../../components/forms/Checkbox/Checkbox.jsx";
import TrucksTable from "../../../components/tables/TrucksTable/TrucksTable.jsx";
import BulkImportModal from "../../../components/bulk/BulkImportModal.jsx";
import ConfirmModal from "../../../components/ui/Modal/ConfirmModal.jsx";
import BulkDeleteBar from "../../../components/ui/BulkDeleteBar/BulkDeleteBar.jsx";
import { RATES, WEIGHTS, visibleMonthOptions } from "../../../data/rates.js";
import { useApp } from "../../../hooks/useApp.js";
import { useRowSelection } from "../../../hooks/useRowSelection.js";
import { isValidVin, sanitizeVinInput } from "../../../utils/validators.js";
import { parseYesNo } from "../../../utils/bulkImport.js";
import { money } from "../../../utils/format.js";
import { getToken } from "../../../services/api.js";
import { getWeightCategories, getFirstUsedMonths, getTaxYears } from "../../../services/lookupService.js";
import {
  getReturnForBusiness,
  updateReturnMonth,
  addVehicle,
  updateVehicle,
  deleteVehicle,
  deleteVehiclesBulk,
  bulkAddVehicles,
} from "../../../services/trucksService.js";
import { updateReturnYearMonth } from "../../../services/returnService.js";

// Only TY2026 is offered for now - matches the current backend seed data
// (lookup_tax_years). Kept as the initial state so the field still renders
// immediately if the lookup fetch is slow or fails.
const DEFAULT_TAX_YEARS = [{ value: "2026" }];
const yearLabel = (year) => `${year}-${Number(year) + 1}`;

const TrucksStep = ({ onNext, onBack }) => {
  const { state, dispatch, showToast } = useApp();
  const [vin, setVin] = useState("");
  const [cat, setCat] = useState("");
  const [logging, setLogging] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [agri, setAgri] = useState(false);
  const [vinError, setVinError] = useState(false);
  const [catError, setCatError] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [monthSaving, setMonthSaving] = useState(false);
  const [yearSaving, setYearSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [loadingReturn, setLoadingReturn] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [removeError, setRemoveError] = useState("");
  const localVinsRef = useRef(null);

  // Checkbox multi-select + bulk delete - fully separate from the single-row
  // pendingRemove/removeError state above, so the existing one-at-a-time
  // Delete flow is untouched; this just adds a second path alongside it.
  const selection = useRowSelection();
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkError, setBulkError] = useState("");

  const usingBackend = !!(getToken() && state.returnId);
  const isEditing = editingId != null;
  const isFinalReturn = !!state.finalReturn || state.returnType === "final";

  // Rejected-return-resubmission vehicle info (informational only - adding more
  // vehicles than originally filed is allowed; an additional service fee just
  // applies before resubmitting, enforced server-side at submit time by
  // Return2290XmlServiceImpl.requireReturnIsPaidFor). hasRejectionInfo is false
  // for a plain draft (state.originalVehicleCount is null until first submit,
  // unchanged existing behavior) and for a rejected return that predates this
  // field (never captured a baseline either).
  const hasRejectionInfo = state.rejectedResubmission && state.originalVehicleCount != null;
  const extraPendingFee = state.extraVehiclesPendingFee ?? 0;

  // Dropdown values from the backend (Requirement 3: no hardcoded dropdowns)
  // - falls back to the static data/rates.js lists only for the
  // unauthenticated dashboard-demo path or while the fetch is in flight.
  useEffect(() => {
    let cancelled = false;
    Promise.all([getFirstUsedMonths(), getTaxYears()])
      .then(([months, taxYears]) => {
        if (cancelled) return;
        if (Array.isArray(months) && months.length) {
          dispatch({ type: "SET_LOOKUPS", months });
        }
        if (Array.isArray(taxYears) && taxYears.length) {
          dispatch({ type: "SET_LOOKUPS", taxYears: taxYears.map((y) => ({ value: y.code })) });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Weight-category rates depend on the selected "Month first used": the IRS
  // partial_period_rate table charges a different amount per month (July =
  // full annual rate; Aug-Jun = that month's prorated figure), so this
  // refetches every time state.month changes rather than once on mount -
  // otherwise the Weight category dropdown would always show the annual
  // rate even for a truck first used mid-year.
  useEffect(() => {
    let cancelled = false;
    setLookupsLoading(true);
    getWeightCategories(state.month)
      .then((weightCategories) => {
        if (cancelled) return;
        if (Array.isArray(weightCategories) && weightCategories.length) {
          dispatch({ type: "SET_LOOKUPS", weightCategories });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLookupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.month]);

  // Loads (or creates) the current business's draft return, with its trucks
  // and server-computed totals - the backend is the source of truth once
  // this has loaded (see useTotals.js).
  useEffect(() => {
    // Guards on returnTotals, not returnId: returnId survives a page
    // refresh (persisted in AppContext), but returnTotals/trucks don't, so
    // checking returnId here would wrongly skip reloading them.
    if (!getToken() || !state.businessId || state.returnTotals) return;
    let cancelled = false;
    setLoadingReturn(true);
    getReturnForBusiness(state.businessId)
      .then((r) => {
        if (!cancelled) dispatch({ type: "SET_RETURN", ...r });
      })
      .catch((err) => {
        if (!cancelled) showToast(err.message || "Couldn't load your trucks. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoadingReturn(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.businessId]);

  const monthOptions = state.monthsLookup.length
    ? state.monthsLookup.map((m) => [m.value, m.label])
    : visibleMonthOptions().map((m) => [m[0], m[1]]);

  const taxYearOptions = state.taxYearsLookup.length ? state.taxYearsLookup : DEFAULT_TAX_YEARS;

  const catOptions = state.weightCategoriesLookup.length
    ? state.weightCategoriesLookup.map((w) => {
        const annual = logging ? w.annualLoggingAmt : w.annualNormalAmt;
        const period = logging ? w.periodLoggingAmt ?? annual : w.periodNormalAmt ?? annual;
        const isPartialPeriod = Number(period) !== Number(annual);
        return [
          w.code,
          `${w.code} · ${w.weightLabel} · ${money(period)}${isPartialPeriod ? " (this period)" : "/yr"}`,
        ];
      })
    : Object.keys(RATES).map((c) => [c, `${c} · ${WEIGHTS[c]} · ${money(RATES[c][logging ? 1 : 0])}/yr`]);

  const validCatCodes = useMemo(
    () => new Set(state.weightCategoriesLookup.length ? state.weightCategoriesLookup.map((w) => w.code) : Object.keys(RATES)),
    [state.weightCategoriesLookup]
  );

  // "CODE - weight range" strings for the bulk-upload template's Excel
  // dropdown - a bare letter alone doesn't tell someone filling in 50 trucks
  // which category is which, so the dropdown carries the weight band too.
  // catBulkColumn.parse (below) strips the " - ..." part back off before
  // saving, since the backend only wants the code.
  const catDropdownOptions = useMemo(
    () =>
      (state.weightCategoriesLookup.length
        ? state.weightCategoriesLookup.map((w) => [w.code, w.weightLabel])
        : Object.keys(RATES).map((c) => [c, WEIGHTS[c]])
      )
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([code, label]) => `${code} - ${label}`),
    [state.weightCategoriesLookup]
  );

  // Bulk-upload columns for the "+ Add truck" table - keys match the truck
  // payload shape ({ vin, cat, logging, suspended, agri }) that both
  // submitTruck (manual) and bulkAddRow (below) pass to addVehicle/dispatch,
  // so no separate mapping step is needed between the parsed row and the
  // real add-a-truck call.
  const bulkColumns = useMemo(
    () => [
      {
        key: "vin",
        header: "VIN",
        required: true,
        example: "1FUJGLDR9CLBP8834",
        description: "Vehicle Identification Number, usually 17 characters. Letters and numbers only.",
        parse: (v) => sanitizeVinInput(String(v ?? "")),
        validate: (v) => (isValidVin(v) ? "" : "Not a valid VIN."),
      },
      {
        key: "cat",
        header: "Weight category (A-W)",
        required: true,
        example: "D",
        description: "Single letter A-W weight category. Leave blank if Suspended = Yes (it's set to W automatically). Pick from the dropdown - it shows each code's weight range.",
        dropdown: catDropdownOptions,
        parse: (v) => String(v ?? "").trim().split(" - ")[0].trim().toUpperCase(),
        validate: (v, row) => {
          if (row.suspended) return "";
          if (!v) return "Weight category is required unless Suspended = Yes.";
          if (!validCatCodes.has(v)) return "Not a recognized weight category.";
          return "";
        },
      },
      {
        key: "logging",
        header: "Logging (Yes/No)",
        required: false,
        example: "No",
        description: "Yes if this truck is used exclusively for hauling harvested forest products (lower tax rate).",
        parse: parseYesNo,
      },
      {
        key: "suspended",
        header: "Suspended (Yes/No)",
        required: false,
        example: "No",
        description: "Yes if the truck is expected to stay under the mileage limit - listed but not taxed (category W).",
        parse: parseYesNo,
      },
      {
        key: "agri",
        header: "Agricultural (Yes/No)",
        required: false,
        example: "No",
        description: "Only used when Suspended = Yes: Yes = 7,500-mile agricultural limit, No = 5,000-mile regular limit.",
        parse: parseYesNo,
      },
    ],
    [validCatCodes, catDropdownOptions]
  );

  // Same usingBackend/local-fallback branch submitTruck uses, minus the
  // per-field form state - called once per parsed row by BulkImportModal.
  // The local-fallback duplicate-VIN check needs a running set (not just
  // state.trucks) because dispatches inside this loop don't necessarily
  // re-render between rows - localVinsRef tracks VINs added so far in this
  // batch and is cleared whenever the modal is (re)opened.
  const bulkAddRow = async (payload) => {
    if (usingBackend) {
      const r = await addVehicle(state.returnId, payload);
      dispatch({ type: "SET_RETURN", ...r });
      return r;
    }
    if (!localVinsRef.current) localVinsRef.current = new Set(state.trucks.map((t) => t.vin));
    if (localVinsRef.current.has(payload.vin)) {
      throw new Error("That VIN is already on this return.");
    }
    localVinsRef.current.add(payload.vin);
    dispatch({ type: "ADD_TRUCK", truck: payload });
    return payload;
  };

  // Real bulk endpoint (one request for every row) - used instead of
  // bulkAddRow's per-row loop whenever a backend return is loaded.
  const bulkAdd = async (rows) => {
    const r = await bulkAddVehicles(state.returnId, rows);
    dispatch({ type: "SET_RETURN", ...r });
    return r.results;
  };

  const handleYearChange = async (e) => {
    const year = e.target.value;
    if (usingBackend) {
      setYearSaving(true);
      try {
        const r = await updateReturnYearMonth(state.returnId, { taxYear: year, firstUsedMonth: state.month });
        dispatch({ type: "SET_RETURN", ...r });
      } catch (err) {
        showToast(err.message || "Couldn't save the tax year. Please try again.");
      } finally {
        setYearSaving(false);
      }
      return;
    }
    dispatch({ type: "SET_TAX_YEAR", year });
  };

  const handleMonthChange = async (e) => {
    const month = e.target.value;
    if (usingBackend) {
      setMonthSaving(true);
      try {
        const r = await updateReturnMonth(state.returnId, month);
        dispatch({ type: "SET_RETURN", ...r });
      } catch (err) {
        showToast(err.message || "Couldn't update the month. Please try again.");
      } finally {
        setMonthSaving(false);
      }
      return;
    }
    dispatch({ type: "SET_MONTH", month });
  };

  const resetForm = () => {
    setVin("");
    setCat("");
    setLogging(false);
    setSuspended(false);
    setAgri(false);
    setVinError(false);
    setCatError(false);
    setEditingId(null);
  };

  const startEdit = (i) => {
    const t = state.trucks[i];
    if (!t) return;
    setVin(t.vin || "");
    setCat(t.suspended ? "" : t.cat || "");
    setLogging(!!t.logging);
    setSuspended(!!t.suspended);
    setAgri(!!t.agri);
    setVinError(false);
    setCatError(false);
    setEditingId(t.id);
  };

  const applyFieldErrors = (err) => {
    const fieldErrors = err?.body?.error?.errors;
    if (!Array.isArray(fieldErrors)) return;
    if (fieldErrors.some((e) => e.field === "vin")) setVinError(true);
    if (fieldErrors.some((e) => e.field === "vehicleCategoryCd")) setCatError(true);
  };

  const submitTruck = async () => {
    const v = vin.trim().toUpperCase();
    const vinOk = isValidVin(v);
    setVinError(!vinOk);
    if (!vinOk) return;
    if (!suspended && !cat) {
      setCatError(true);
      return;
    }
    const payload = { vin: v, cat: suspended ? "W" : cat, logging, suspended, agri: suspended ? agri : false };

    if (usingBackend) {
      setSaving(true);
      try {
        const r = isEditing ? await updateVehicle(editingId, payload) : await addVehicle(state.returnId, payload);
        dispatch({ type: "SET_RETURN", ...r });
        resetForm();
      } catch (err) {
        applyFieldErrors(err);
        showToast(err.message || "Couldn't save the truck. Please try again.");
      } finally {
        setSaving(false);
      }
      return;
    }

    // Unauthenticated / no backend return loaded yet - local-only fallback (dashboard demo path).
    if (state.trucks.some((t) => t.vin === v)) {
      showToast("That VIN is already on this return.");
      return;
    }
    dispatch({ type: "ADD_TRUCK", truck: payload });
    resetForm();
  };

  const removeTruck = (i) => {
    const t = state.trucks[i];
    if (usingBackend && t?.id) {
      setRemoveError("");
      setPendingRemove(t);
      return;
    }
    dispatch({ type: "REMOVE_TRUCK", index: i });
  };

  const cancelRemoveTruck = () => {
    setPendingRemove(null);
    setRemoveError("");
  };

  const confirmRemoveTruck = async () => {
    const t = pendingRemove;
    if (!t) return;
    setDeletingId(t.id);
    try {
      const r = await deleteVehicle(t.id);
      dispatch({ type: "SET_RETURN", ...r });
      if (editingId === t.id) resetForm();
      setPendingRemove(null);
      setRemoveError("");
    } catch (err) {
      setRemoveError(err.message || "Couldn't delete the truck. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  // Bulk delete - selection.selectedIds holds real backend ids when
  // usingBackend (every row has one, see TrucksTable's rowKey) or array
  // indices in the local/no-backend fallback (rows have no id there),
  // exactly like the single-row removeTruck/confirmRemoveTruck split above.
  // Unlike single-row delete, this always confirms first regardless of mode
  // - bulk removes more than one thing at once, so it gets the same
  // guardrail either way.
  const openBulkDelete = () => {
    setBulkError("");
    setBulkConfirmOpen(true);
  };

  const cancelBulkDelete = () => {
    setBulkConfirmOpen(false);
    setBulkError("");
  };

  const confirmBulkDelete = async () => {
    const count = selection.count;
    setBulkDeleting(true);
    try {
      if (usingBackend) {
        const r = await deleteVehiclesBulk(state.returnId, Array.from(selection.selectedIds));
        dispatch({ type: "SET_RETURN", ...r });
      } else {
        dispatch({ type: "BULK_REMOVE_TRUCKS", indices: selection.selectedIds });
      }
      selection.clear();
      setBulkConfirmOpen(false);
      setBulkError("");
      showToast(`${count} truck${count === 1 ? "" : "s"} deleted successfully.`);
    } catch (err) {
      // Keep the selection intact on failure (Requirement 18) - the confirm
      // modal stays open with the error shown, same UX as the single-row
      // ConfirmModal's error branch above.
      setBulkError(err.message || "Unable to delete the selected trucks. Please try again.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleContinue = () => {
    // Final returns may legitimately have zero vehicles (F2290-027-01 only
    // requires a VIN when the Final checkbox is NOT checked) - every other
    // filer here (Original, mileage-exceeded Amended) must list at least one.
    if (!isFinalReturn && !state.trucks.length) {
      showToast("Add at least one truck to continue.");
      return;
    }
    onNext();
  };

  const formBusy = saving || loadingReturn || deletingId != null;

  return (
    <Panel padded>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
         Trucks
      </div>
      <h2 className="text-2xl m-[4px_0]">Add your trucks</h2>
      <p className="text-[var(--muted)] max-w-[62ch] mb-4">
        {isFinalReturn
          ? "Optional for a final return. Only add a truck if you're still reporting tax for this period — otherwise, continue straight to the summary."
          : "Enter each truck's VIN and weight category. We apply the right rate — including the lower logging rate and $0 for suspended trucks — automatically."}
      </p>

      {hasRejectionInfo && (
        <div
          className="rounded-[var(--r-sm)] border p-3 mb-4 text-[13px]"
          style={
            extraPendingFee > 0
              ? { borderColor: "var(--amber)", background: "var(--amber-soft)", color: "var(--amber)" }
              : { borderColor: "var(--line-2)", background: "var(--panel-2)", color: "var(--muted)" }
          }
        >
          <b className="font-semibold" style={{ color: extraPendingFee > 0 ? "var(--amber)" : "var(--ink)" }}>
            Vehicles: {state.trucks.length} (originally filed: {state.originalVehicleCount})
          </b>
          <div className="mt-0.5">
            {extraPendingFee > 0
              ? `You've added ${extraPendingFee} vehicle${extraPendingFee === 1 ? "" : "s"} beyond what was originally filed — an additional service fee applies before this return can be resubmitted (you'll pay it on the Payment Details step).`
              : `This return was rejected by the IRS. You can add, remove, or correct vehicles freely — adding more than the ${state.originalVehicleCount} originally filed will require an additional service fee before resubmitting.`}
          </div>
        </div>
      )}

      <div className="grid gap-3.5 sm:grid-cols-2" style={{ maxWidth: 560 }}>
        <FormField label="Tax year" hint={yearSaving ? "Saving…" : undefined}>
          <Select value={state.year} onChange={handleYearChange} disabled={yearSaving || loadingReturn}>
            {taxYearOptions.map((y) => (
              <option key={y.value} value={y.value}>
                {yearLabel(y.value)}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField
          label="Month first used (this return)"
          hint={monthSaving ? "Saving…" : "Sets the due date and any part-year tax."}
        >
          <Select value={state.month} onChange={handleMonthChange} disabled={monthSaving || loadingReturn}>
            {monthOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </FormField>
      </div>

        <Panel tone="soft" className="!shadow-none mt-4 p-[22px]">
        <div className="flex items-center justify-between mb-3.5">
          <h3 className="text-[19px]">
            {isEditing ? "Update this truck" : "Add a truck"}
          </h3>
          {!isEditing && (
            <Button variant="ghost" onClick={() => setBulkOpen(true)} disabled={formBusy}>
              ⇪ Bulk upload
            </Button>
          )}
        </div>
        <div className="grid gap-3.5 sm:grid-cols-2">
          <FormField
            label="VIN"
            hint="Letters and numbers, usually 17 characters. Use the truck's VIN, not the trailer's."
            error="That doesn't look like a valid VIN."
            showError={vinError}
          >
            <TextInput
              mono
              maxLength={19}
              placeholder="1FUJGLDR9CLBP8834"
              value={vin}
              onChange={(e) => setVin(sanitizeVinInput(e.target.value))}
              invalid={vinError}
              disabled={formBusy}
            />
          </FormField>
          <FormField label="Weight category" error="Pick a category." showError={catError} hint={lookupsLoading ? "Loading options…" : undefined}>
            <Select
              value={cat}
              disabled={suspended || formBusy || lookupsLoading}
              onChange={(e) => {
                setCat(e.target.value);
                setCatError(false);
              }}
              invalid={catError}
            >
              <option value="">Select weight category…</option>
              {catOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormField>
        </div>

        <Checkbox
          id="tLog"
          title="Logging truck"
          subtitle="Hauls harvested forest products — pays 25% less."
          checked={logging}
          onChange={(e) => setLogging(e.target.checked)}
          disabled={formBusy}
        />
        <Checkbox
          id="tSusp"
          title="Suspended (low mileage)"
          subtitle="Listed but not taxed — category W. Pick the mileage limit below."
          checked={suspended}
          onChange={(e) => {
            setSuspended(e.target.checked);
            if (e.target.checked) setCat("");
            else setAgri(false);
          }}
          disabled={formBusy}
        />
        {suspended && (
          <FormField
            label="Mileage limit"
            hint="Sets the Line 7 checkbox: 5,000 miles, or 7,500 for agricultural vehicles."
            style={{ maxWidth: 440, marginTop: 8 }}
          >
            <Select value={agri ? "agri" : "regular"} onChange={(e) => setAgri(e.target.value === "agri")} disabled={formBusy}>
              <option value="regular">Used 5,000 miles or less</option>
              <option value="agri">Agricultural — used 7,500 miles or less</option>
            </Select>
          </FormField>
        )}

        <div className="flex gap-2">
          <Button variant="ghost" onClick={submitTruck} disabled={formBusy}>
            {saving ? "Saving…" : isEditing ? "Update truck" : "+ Add truck"}
          </Button>
          {isEditing && (
            <Button variant="ghost" onClick={resetForm} disabled={formBusy}>
              Cancel
            </Button>
          )}
        </div>
      </Panel>

      {loadingReturn ? (
        <p className="text-[var(--muted)] text-sm mt-4">Loading your trucks…</p>
      ) : (
        <>
          <BulkDeleteBar count={selection.count} onDelete={openBulkDelete} loading={bulkDeleting} className="mt-4" />
          <TrucksTable
            trucks={state.trucks}
            month={state.month}
            onRemove={removeTruck}
            onEdit={usingBackend ? startEdit : undefined}
            weightCategories={state.weightCategoriesLookup}
            selectedIds={selection.selectedIds}
            onToggleRow={selection.toggle}
            onToggleAll={selection.selectAll}
          />
        </>
      )}

      <div className="flex justify-between gap-3 mt-6">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <div className="flex gap-3">
          <Button onClick={handleContinue}>Continue</Button>
        </div>
      </div>

      <BulkImportModal
        open={bulkOpen}
        onClose={() => {
          setBulkOpen(false);
          localVinsRef.current = null;
        }}
        title="Bulk upload trucks"
        columns={bulkColumns}
        filename="form2290-trucks-template.xlsx"
        sheetName="Vehicles"
        addRow={bulkAddRow}
        bulkAdd={usingBackend ? bulkAdd : undefined}
        templateId="form2290-original-trucks-v1"
      />

      <ConfirmModal
        open={!!pendingRemove}
        title="Delete Confirmation"
        confirmLabel="Delete"
        loading={deletingId === pendingRemove?.id}
        error={removeError}
        onConfirm={confirmRemoveTruck}
        onCancel={cancelRemoveTruck}
      >
        Are you sure you want to delete this record? This action cannot be undone.
      </ConfirmModal>

      <ConfirmModal
        open={bulkConfirmOpen}
        title="Delete Selected Records?"
        confirmLabel="Delete"
        loading={bulkDeleting}
        error={bulkError}
        onConfirm={confirmBulkDelete}
        onCancel={cancelBulkDelete}
      >
        Are you sure you want to delete these {selection.count} record{selection.count === 1 ? "" : "s"}? This action
        cannot be undone.
      </ConfirmModal>
    </Panel>
  );
};

export default TrucksStep;
